import React from 'react';
import { Award, Users } from 'lucide-react';
import type { BrokerPerformance as BrokerPerformanceType } from '../../../types/acquisitionAnalysis';

interface BrokerPerformanceProps {
  data: BrokerPerformanceType;
}

export const BrokerPerformance: React.FC<BrokerPerformanceProps> = ({ data }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-indigo-400" />
          Broker Performance {data.fiscalYearLabel ? `(${data.fiscalYearLabel})` : ''}
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-900/50">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider w-16">
                  Rank
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Broker
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Annual Trail
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  % of Book
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {data.topBrokers.map((broker) => (
                <tr key={broker.rank} className="hover:bg-slate-700/30 transition-colors">
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${
                        broker.rank === 1
                          ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30'
                          : broker.rank === 2
                          ? 'bg-slate-400/20 text-slate-300 border border-slate-400/30'
                          : broker.rank === 3
                          ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                          : 'bg-slate-700 text-slate-400'
                      }`}
                    >
                      {broker.rank}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-white">
                    {broker.brokerName}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-indigo-400">
                    {formatCurrency(broker.annualTrail)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-slate-300">
                    {formatPercent(broker.percentOfBook)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-green-400" />
          Broker Retention Metrics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-sm text-slate-400 mb-1">New Brokers Added</p>
            <p className="text-2xl font-bold text-green-300">
              {data.brokerRetention.newBrokers}
            </p>
          </div>
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-sm text-slate-400 mb-1">Brokers Left</p>
            <p className="text-2xl font-bold text-red-300">
              {data.brokerRetention.leftBrokers}
            </p>
          </div>
          <div
            className={`p-4 rounded-lg border ${
              data.brokerRetention.attrition === 0
                ? 'bg-emerald-500/10 border-emerald-500/30'
                : data.brokerRetention.attrition < 10
                ? 'bg-yellow-500/10 border-yellow-500/30'
                : 'bg-red-500/10 border-red-500/30'
            }`}
          >
            <p className="text-sm text-slate-400 mb-1">Attrition Rate</p>
            <p
              className={`text-2xl font-bold ${
                data.brokerRetention.attrition === 0
                  ? 'text-emerald-300'
                  : data.brokerRetention.attrition < 10
                  ? 'text-yellow-300'
                  : 'text-red-300'
              }`}
            >
              {data.brokerRetention.attrition === 0
                ? 'Zero'
                : `${formatPercent(data.brokerRetention.attrition)}`}
            </p>
          </div>
        </div>
      </div>

      {data.brokerRetention.attrition === 0 && (
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <p className="text-sm text-emerald-300">
            <strong>Excellent Retention:</strong> Zero broker attrition is a strong positive
            signal indicating a healthy business culture and satisfied brokers. This reduces
            acquisition risk significantly.
          </p>
        </div>
      )}

      {data.topBrokers[0] && data.topBrokers[0].percentOfBook > 50 && (
        <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <p className="text-sm text-yellow-300">
            <strong>Concentration Risk:</strong> The top broker represents{' '}
            {formatPercent(data.topBrokers[0].percentOfBook)} of the trail book. Consider the
            impact if this broker were to leave post-acquisition.
          </p>
        </div>
      )}
    </div>
  );
};
